import { useCallback, useState } from "react";
import { researchCms } from "@/api/researchCms.service";
import { SearchModal, searchModalStyles } from "@/components/ResearchCms/SearchModal/SearchModal";
import styles from "./BpaPickerField.module.css";

type RankingEntity = { id: string; name: string; type: string };

type RankingEntityPickerFieldProps = {
  value: string | null;
  onChange: (entityId: string | null) => void;
  onEntityChange?: (entity: RankingEntity | null) => void;
  onCreateNew?: () => void;
  selectedLabel?: string | null;
  disabled?: boolean;
  error?: string;
};

const LIMIT = 50;

export function RankingEntityPickerField({
  value,
  onChange,
  onEntityChange,
  onCreateNew,
  selectedLabel,
  disabled,
  error,
}: RankingEntityPickerFieldProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<RankingEntity[]>([]);
  const [searching, setSearching] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const search = useCallback(async (term: string) => {
    setSearching(true);
    setOffset(0);
    setHasMore(false);
    try {
      const data = await researchCms.getRankingEntities({
        search: term,
        limit: LIMIT,
        offset: 0,
      });
      const entities = data.researchRankingEntities.map(e => ({
        id: e.id,
        name: e.name,
        type: e.type,
      }));
      setResults(entities);
      setHasMore(entities.length === LIMIT);
      setOffset(LIMIT);
    } catch (err) {
      console.error("Ranking entity search failed:", err);
      setResults([]);
      setHasMore(false);
    } finally {
      setSearching(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const data = await researchCms.getRankingEntities({
        search: query,
        limit: LIMIT,
        offset,
      });
      const entities = data.researchRankingEntities.map(e => ({
        id: e.id,
        name: e.name,
        type: e.type,
      }));
      setResults(prev => [...prev, ...entities]);
      setHasMore(entities.length === LIMIT);
      setOffset(prev => prev + LIMIT);
    } catch (err) {
      console.error("Load more ranking entities failed:", err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [query, offset, loadingMore, hasMore]);

  function handleOpen() {
    if (disabled) return;
    setQuery("");
    setResults([]);
    setOffset(0);
    setHasMore(false);
    setOpen(true);
    search("");
  }

  function handleSelect(entity: RankingEntity) {
    onChange(entity.id);
    onEntityChange?.(entity);
    setOpen(false);
  }

  function handleClear() {
    onChange(null);
    onEntityChange?.(null);
  }

  return (
    <div className={styles.wrapper}>
      <span className={styles.label}>
        Ranking entity{" "}
        <span aria-hidden="true" style={{ color: "var(--color-danger, #ef4444)" }}>
          *
        </span>
      </span>
      <div className={styles.triggerRow}>
        <button type="button" className={styles.trigger} onClick={handleOpen} disabled={disabled}>
          {value && selectedLabel ? (
            <span className={styles.selectedText}>{selectedLabel}</span>
          ) : (
            <span className={styles.placeholder}>Search entities...</span>
          )}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={styles.chevron}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
        {value && !disabled && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
            aria-label="Clear ranking entity"
          >
            ×
          </button>
        )}
      </div>
      {error && (
        <span className={styles.helperText} style={{ color: "var(--color-danger, #ef4444)" }}>
          {error}
        </span>
      )}

      <SearchModal
        open={open}
        onClose={() => setOpen(false)}
        title="Select ranking entity"
        query={query}
        onQueryChange={setQuery}
        onSearch={search}
        debounceMs={300}
        onLoadMore={loadMore}
        loadingMore={loadingMore}
        placeholder="Search ranking entities..."
        searching={searching}
        hasResults={results.length > 0}
        resultCount={results.length}
        idleMessage="Search for a ranking entity"
        idleHint="Results will appear as you type"
        footerSlot={
          onCreateNew ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onCreateNew();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "10px 12px",
                border: "1px dashed var(--color-border, #e2e8f0)",
                borderRadius: 6,
                background: "transparent",
                color: "var(--color-primary, #3b82f6)",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                width: "100%",
                textAlign: "left" as const,
              }}
            >
              + Create new ranking entity
            </button>
          ) : undefined
        }
      >
        {results.map(entity => (
          <div
            key={entity.id}
            className={searchModalStyles.resultItem}
            onClick={() => handleSelect(entity)}
          >
            <div className={searchModalStyles.resultInfo}>
              <span className={searchModalStyles.resultName}>{entity.name}</span>
              <span className={searchModalStyles.resultSubtitle}>{entity.type}</span>
            </div>
          </div>
        ))}
      </SearchModal>
    </div>
  );
}
